import {
  laravelGet,
  laravelPost,
  laravelPut,
  laravelDelete,
  laravelRequest,
} from '../utils/laravelClient';
import { ApiError } from '../utils/laravelError';

const toSnakeCase = (obj) => {
  const snakeCaseObj = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    snakeCaseObj[snakeKey] = value;
  }
  return snakeCaseObj;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => toCamelCase(item));
  }

  const camelCaseObj = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    camelCaseObj[camelKey] =
      typeof value === 'object' && value !== null ? toCamelCase(value) : value;
  }
  return camelCaseObj;
};

const buildPromotionsPath = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('per_page', params.limit);
  if (params.active !== undefined) queryParams.append('active', params.active);
  const qs = queryParams.toString();
  return qs ? `/promotions?${qs}` : '/promotions';
};

export const fetchPromotions = async (params = {}) => {
  const data = await laravelRequest('GET', buildPromotionsPath(params), { auth: 'admin' });

  if (data?.data && data.current_page) {
    return {
      data: data.data.map((item) => toCamelCase(item)),
      total: data.total,
      page: data.current_page,
      limit: data.per_page,
    };
  }

  if (Array.isArray(data)) {
    return {
      data: data.map((item) => toCamelCase(item)),
      total: data.length,
      page: 1,
      limit: data.length,
    };
  }

  return toCamelCase(data);
};

export const fetchActivePromotions = async () => {
  try {
    const data = await laravelGet('/promotions?active=1', { auth: 'none' });
    return Array.isArray(data) ? data.map((item) => toCamelCase(item)) : [];
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return [];
    }
    if (error?.message?.includes('Failed to fetch')) {
      return [];
    }
    throw error;
  }
};

export const fetchPromotionById = async (id) => {
  const data = await laravelGet(`/promotions/${id}`, { auth: 'admin' });
  return toCamelCase(data);
};

export const createPromotion = async (promotionData) => {
  try {
    return await laravelPost('/promotions', toSnakeCase(promotionData), { auth: 'admin' });
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      throw new Error('Backend API endpoint not implemented. Please implement: POST /api/promotions');
    }
    if (error?.message?.includes('Failed to fetch')) {
      throw new Error('Cannot connect to API. Please check if the backend is running.');
    }
    throw error;
  }
};

export const updatePromotion = async (id, promotionData) => {
  return laravelPut(`/promotions/${id}`, toSnakeCase(promotionData), { auth: 'admin' });
};

export const deletePromotion = async (id) => {
  return laravelDelete(`/promotions/${id}`, { auth: 'admin' });
};

export const togglePromotionStatus = async (id) => {
  const promotion = await fetchPromotionById(id);
  const backendData = toSnakeCase({
    ...promotion,
    isActive: !promotion.isActive,
  });
  return laravelPut(`/promotions/${id}`, backendData, { auth: 'admin' });
};
